/**
 * @garden/web — React 19 entrypoint.
 *
 * Composes the provider stack:
 *   - <StrictMode>            React 19 dev-mode double-invocation
 *   - <QueryClientProvider>   TanStack Query v5
 *   - <I18nextProvider>       react-i18next, RU default
 *   - <BrowserRouter>         React Router v7
 *   - <App />                 routes
 *   - <Toaster />             shadcn/Sonner toast portal
 *
 * Strict CSP, no inline scripts (REQ-PRD-§11.2-C) — Vite emits the
 * bundle as an external module; this file imports the CSS entry once
 * so the build picks it up.
 *
 * API base-URL wiring (STORY-000-011):
 *   `configureApiClient({ baseUrl })` is called ONCE at startup, before
 *   `createRoot(...).render(...)`, pinning the API base URL at a single,
 *   testable seam rather than relying solely on the module-load default
 *   in `api-client.ts`. The resolution (`resolveApiBaseUrl`) mirrors that
 *   default so behaviour is unchanged when `VITE_API_BASE_URL` is unset:
 *   the env override wins; else `/api` in dev (forwarded by the Vite
 *   `server.proxy`); else `""` (prod/test same-origin). The dev `/api`
 *   proxy is the local same-origin default; `VITE_API_BASE_URL` is the
 *   cross-origin escape hatch for non-same-origin / E2E deploys. No
 *   committed `.env` file — it is an opt-in env override.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter } from "react-router-dom";

import { App } from "@/app/App";
import { IdleLogoutController } from "@/app/IdleLogoutController";
import { ThemeController } from "@/app/ThemeController";
import { Toaster } from "@/components/ui/toaster";
import { loginDoorForPath } from "@/features/auth/destination";
import { GuestClaimController } from "@/features/chat/guest/GuestClaimController";
import { endLocalSession } from "@/features/chat/session-end";
import { bootstrapAuth, configureApiClient } from "@/lib/api-client";
import { installAnalyticsLifecycleListeners } from "@/lib/analytics/lifecycleListeners";
import i18n, { initI18n } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";

import "@/styles/tailwind.css";

/**
 * Resolve the API base URL from build-time env, mirroring the
 * `DEFAULT_CONFIG.baseUrl` resolution in `api-client.ts`:
 *   - `VITE_API_BASE_URL` (when set) — the cross-origin escape hatch,
 *   - else `/api` in `development` (the Vite `server.proxy` stitches it
 *     to the FastAPI service),
 *   - else `""` (production + test — CloudFront serves the API
 *     same-origin in prod; tests stub fetch).
 *
 * Reads `import.meta.env` at call time so the unit test can drive it via
 * `vi.stubEnv` (STORY-000-011 AC #5/#6).
 */
export function resolveApiBaseUrl(): string {
  return (
    import.meta.env.VITE_API_BASE_URL ??
    (import.meta.env.MODE === "development" ? "/api" : "")
  );
}

/**
 * Pin the API base URL once. Exported so the test can assert the exact
 * `configureApiClient` + `getApiClientConfig().baseUrl` round-trip the
 * app performs at startup, and so the render side-effect below stays
 * out of the test import path.
 *
 * SCRUM-145 — the persistent-401 bounce is door-aware: an admin or seller
 * whose session dies mid-session goes back to THEIR door, not the consumer
 * `/login` (Track 0 persona-auth Task 6, "logout by door"). When the tab is
 * already sitting on a login door we keep the `return_to` it already carries
 * rather than wrapping the door URL inside itself.
 */
export function bootstrapApiClient(): void {
  configureApiClient({
    baseUrl: resolveApiBaseUrl(),
    onSessionEnded: endLocalSession,
    onUnauthorizedRedirect: (returnTo) => {
      const path = window.location.pathname;
      const door = loginDoorForPath(path);
      if (path === door) return; // already at the right door — don't re-wrap
      window.location.assign(door + "?return_to=" + encodeURIComponent(returnTo));
    },
  });
}

// Side-effecting boot — guarded so importing this module under Vitest
// (`MODE === "test"`) does NOT mount the React tree. Production and the
// dev server both run the real bootstrap + render.
if (import.meta.env.MODE !== "test") {
  initI18n();
  bootstrapApiClient();
  // Silent refresh on boot: the access token lives in JS memory only, so a
  // hard reload starts unauthenticated even with a valid HttpOnly refresh
  // cookie. Fire-and-forget — it sets the token (if the cookie is valid) and
  // flips the store's `bootstrapped` flag; RequireAuth waits on that flag so a
  // logged-in user isn't bounced to /login on reload. Must run AFTER
  // bootstrapApiClient() so the refresh hits the configured base URL.
  void bootstrapAuth();
  // Flush pending analytics on page-hide / unload (STORY-009-003). Idempotent.
  installAnalyticsLifecycleListeners();

  const rootEl = document.getElementById("root");
  if (!rootEl) {
    throw new Error("Root container #root not found in index.html");
  }

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter>
            {/* Applies data-theme/data-palette to <html> from the UI store. */}
            <ThemeController />
            {/* SCRUM-142 — one-hour idle auto-logout. */}
            <IdleLogoutController />
            {/* SCRUM-274/276 — the guest conversation follows the visitor
                into their account, whichever screen signed them in. */}
            <GuestClaimController />
            <App />
            <Toaster />
          </BrowserRouter>
        </I18nextProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
